import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';

const incrementContractNumberFn = httpsCallable(functions, 'incrementContractNumber');

const CONTRACT_NUMBER_DOC_ID = 'contractNumberSettings';

/**
 * Get the current next contract number
 * @returns {Promise<number>} The next contract number to be used
 */
export const getNextContractNumber = async () => {
  try {
    const docRef = doc(db, 'systemSettings', CONTRACT_NUMBER_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.nextContractNumber || 1;
    } else {
      // Initialize with contract number 1 if document doesn't exist
      await initializeContractNumber();
      return 1;
    }
  } catch (error) {
    console.error('Error getting next contract number:', error);
    // Return 1 as fallback
    return 1;
  }
};

/**
 * Initialize the contract number system with starting number 1
 */
export const initializeContractNumber = async () => {
  try {
    const docRef = doc(db, 'systemSettings', CONTRACT_NUMBER_DOC_ID);
    await setDoc(docRef, {
      nextContractNumber: 1,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log('Initialized contract number system with starting number 1');
  } catch (error) {
    console.error('Error initializing contract number system:', error);
    throw error;
  }
};

/**
 * Increment the next contract number after a contract is signed. The counter is shared
 * by every company, so the server hands it out (transaction) rather than the browser.
 * @returns {Promise<number>} The contract number that was just used
 */
export const incrementContractNumber = async () => {
  try {
    const { data } = await incrementContractNumberFn();
    console.log(`Contract number incremented: used ${data.contractNumber}`);
    return data.contractNumber; // Return the number that was just used
  } catch (error) {
    console.error('Error incrementing contract number:', error);
    throw error;
  }
};

/**
 * Set the next contract number (for admin purposes)
 * @param {number} number - The next contract number to set
 */
export const setNextContractNumber = async (number) => {
  try {
    if (number < 1) {
      throw new Error('Contract number must be at least 1');
    }
    
    const docRef = doc(db, 'systemSettings', CONTRACT_NUMBER_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      await updateDoc(docRef, {
        nextContractNumber: number,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(docRef, {
        nextContractNumber: number,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    console.log(`Next contract number set to: ${number}`);
  } catch (error) {
    console.error('Error setting next contract number:', error);
    throw error;
  }
};

/**
 * Get contract number statistics
 * @returns {Promise<Object>} Contract number statistics
 */
export const getContractNumberStats = async () => {
  try {
    const docRef = doc(db, 'systemSettings', CONTRACT_NUMBER_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        nextContractNumber: data.nextContractNumber || 1,
        lastUpdated: data.updatedAt?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date()
      };
    } else {
      return {
        nextContractNumber: 1,
        lastUpdated: new Date(),
        createdAt: new Date()
      };
    }
  } catch (error) {
    console.error('Error getting contract number stats:', error);
    throw error;
  }
};
